import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  sessionToken: string | null;

  id: string | null;
  userId: string | null;
  organizationId: string | null;

  email: string | null;
  roles: string[];

  isAuthenticated: boolean;
}

const initialState: AuthState = {
  accessToken:
    localStorage.getItem("accessToken"),

  refreshToken:
    localStorage.getItem("refreshToken"),

  sessionToken:
    localStorage.getItem("sessionToken"),

  id: localStorage.getItem("id"),

  userId:
    localStorage.getItem("userId"),

  organizationId:
    localStorage.getItem(
      "organizationId",
    ),

  email:
    localStorage.getItem("email"),

  roles: JSON.parse(
    localStorage.getItem("roles") ??
    "[]",
  ),

  isAuthenticated:
    !!localStorage.getItem(
      "accessToken",
    ),
};

const authSlice = createSlice({
  name: "auth",

  initialState,

  reducers: {
    setAuth: (
      state,
      action: PayloadAction<AuthState>,
    ) => {
      localStorage.setItem(
        "accessToken",
        action.payload.accessToken ??
        "",
      );

      localStorage.setItem(
        "refreshToken",
        action.payload.refreshToken ??
        "",
      );

      localStorage.setItem(
        "sessionToken",
        action.payload.sessionToken ??
        "",
      );

      localStorage.setItem(
        "id",
        action.payload.id ?? "",
      );

      localStorage.setItem(
        "userId",
        action.payload.userId ?? "",
      );

      localStorage.setItem(
        "organizationId",
        action.payload.organizationId ??
        "",
      );

      localStorage.setItem(
        "email",
        action.payload.email ?? "",
      );

      localStorage.setItem(
        "roles",
        JSON.stringify(
          action.payload.roles,
        ),
      );

      state.accessToken =
        action.payload.accessToken;

      state.refreshToken =
        action.payload.refreshToken;

      state.sessionToken =
        action.payload.sessionToken;

      state.id = action.payload.id;

      state.userId =
        action.payload.userId;

      state.organizationId =
        action.payload.organizationId;

      state.email =
        action.payload.email;

      state.roles =
        action.payload.roles;

      state.isAuthenticated =
        true;
    },

    // `organizationId` is which organization the new tokens ADDRESS. The three
    // sign-in callers already pass the whole AuthTokens object, which carries
    // it; switchOrganization passes the org it just switched into. Recording it
    // is what lets OrganizationGrid tell whether the tokens in hand already
    // point at a card before re-fetching them. Optional, so a caller that only
    // rotates tokens (see updateTokens) leaves the organization alone.
    setTokens: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
        sessionToken: string;
        organizationId?: string | null;
      }>,
    ) => {
      const {
        accessToken,
        refreshToken,
        sessionToken,
        organizationId,
      } = action.payload;

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("sessionToken", sessionToken);

      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.sessionToken = sessionToken;

      if (organizationId !== undefined) {
        localStorage.setItem(
          "organizationId",
          organizationId ?? "",
        );
        state.organizationId = organizationId;
      }

      state.isAuthenticated = true;
    },

    updateAccessToken: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.accessToken =
        action.payload;

      localStorage.setItem(
        "accessToken",
        action.payload,
      );
    },

    // Rotated access + refresh pair from a silent refresh (session unchanged).
    updateTokens: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
      }>,
    ) => {
      const { accessToken, refreshToken } =
        action.payload;

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
    },

    logout: (state) => {
      localStorage.removeItem(
        "accessToken",
      );

      localStorage.removeItem(
        "refreshToken",
      );

      localStorage.removeItem(
        "sessionToken",
      );

      localStorage.removeItem("id");

      localStorage.removeItem(
        "userId",
      );

      localStorage.removeItem(
        "organizationId",
      );

      localStorage.removeItem(
        "email",
      );

      localStorage.removeItem(
        "roles",
      );

      Object.assign(
        state,
        initialState,
      );

      state.isAuthenticated =
        false;
    },

  },
});

export const {
  setAuth,
  setTokens,
  updateAccessToken,
  updateTokens,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
